import { useEffect, useRef, useState } from 'react';
import { isTauri, safeInvoke } from '@/utils/tauri';

const getDocumentVisible = () => {
  if (typeof document === 'undefined') {
    return true;
  }
  return !(document.hidden || document.visibilityState === 'hidden');
};

const getWindowFocused = () => {
  if (typeof document === 'undefined') {
    return true;
  }
  // 检查窗口是否有焦点（浏览器环境）
  // 使用 document.hasFocus() 来检测窗口是否真正有焦点
  // 结合 visibilityState 来确保准确性
  return document.visibilityState !== 'hidden' && (document.hasFocus?.() ?? true);
};

export const useAppVisibility = () => {
  const [isVisible, setIsVisible] = useState(() => {
    const docVisible = getDocumentVisible();
    const windowFocused = getWindowFocused();
    return docVisible && windowFocused;
  });
  const tauriVisibleRef = useRef<boolean | null>(null);
  const windowFocusedRef = useRef<boolean>(getWindowFocused());

  useEffect(() => {
    let unlisten: (() => void) | undefined;
    let isMounted = true;

    const updateVisibility = () => {
      const docVisible = getDocumentVisible();
      const windowFocused = getWindowFocused();
      windowFocusedRef.current = windowFocused;
      const tauriVisible = tauriVisibleRef.current ?? true;
      // 在浏览器环境下，需要同时检查文档可见性和窗口焦点
      // 在 Tauri 环境下，主要依赖 tauriVisible
      setIsVisible(docVisible && windowFocused && tauriVisible);
    };

    if (isTauri()) {
      (async () => {
        try {
          const { listen } = await import('@tauri-apps/api/event');
          const initial = await safeInvoke<boolean>('is_window_visible');
          if (typeof initial === 'boolean') {
            tauriVisibleRef.current = initial;
          }
          if (isMounted) {
            updateVisibility();
          }
          unlisten = await listen<boolean>('app-visibility-changed', (event) => {
            tauriVisibleRef.current = !!event.payload;
            updateVisibility();
          });
        } catch (error) {
          console.warn('Failed to listen to app visibility events:', error);
        }
      })();
    }

    // 监听文档可见性变化（标签页切换等）
    document.addEventListener('visibilitychange', updateVisibility);
    
    // 监听窗口焦点变化（切换到其他应用窗口等）
    // 使用 blur/focus 事件作为补充，确保在用户切换到其他应用时也能检测到
    // 注意：这些事件会在窗口失去/获得焦点时触发，包括切换到其他应用
    const handleBlur = () => {
      // 延迟检查，因为 blur 事件触发时 document.hasFocus() 可能还没有更新
      setTimeout(() => {
        windowFocusedRef.current = getWindowFocused();
        updateVisibility();
      }, 0);
    };
    
    const handleFocus = () => {
      windowFocusedRef.current = getWindowFocused();
      updateVisibility();
    };
    
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      if (unlisten) {
        unlisten();
      }
    };
  }, []);

  return isVisible;
};
